import { useState, useEffect, useRef } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { TbEdit } from "react-icons/tb";
import { FaMedal, FaTrophy, FaCamera, FaStar } from "react-icons/fa";
import { API_BASE, BACKEND_BASE } from "../utils/Instance";
import { useAuth } from "../context/AuthContext";
import W9Form from "../components/W9Form";

export default function Profile() {
  const { profile, setProfile } = useAuth();
  const [profileData, setProfileData] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [editMode, setEditMode] = useState({});
  const [galleries, setGalleries] = useState([]);
  const [joinedContests, setJoinedContests] = useState([]);
  const [loadingContests, setLoadingContests] = useState(false);
  const fileInputRef = useRef(null);

  // 🔹 Fetch profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!profile?.accessToken) return;

        const res = await fetch(`${API_BASE}accounts/profile/me/`, {
          headers: { Authorization: `Bearer ${profile.accessToken}` },
        });

        if (!res.ok) throw new Error("Failed to fetch profile");
        const data = await res.json();

        const role = data.role || profile?.role || "user";

        // ✅ Map backend → frontend by role
        if (role === "contributor") {
          setProfileData([
            { id: "legal_full_name", label: "Name", value: data.legal_full_name || "", locked: true },
            { id: "screen_name", label: "Screen Name", value: data.screen_name || profile?.screenName || "", locked: true },
            { id: "age", label: "Age", value: data.age ? `${data.age} Years` : "", locked: true },
            { id: "gender", label: "Gender", value: data.gender || "", locked: true },
            { id: "hair_color", label: "Hair Color", value: data.hair_color || "" },
            { id: "skin_tone", label: "Skin Tone", value: data.skin_tone || "" },
            { id: "height", label: "Height", value: data.height || "" },
            { id: "weight", label: "Weight", value: data.weight || "" },
            { id: "body_type", label: "Body Type", value: data.body_type || "" },
            { id: "shoe_size", label: "Shoe Size", value: data.shoe_size || "" },
            { id: "bust_size", label: "Bust Size", value: data.bust_size || "N/A" },
            { id: "penis_length", label: "Penis Size", value: data.penis_length || "" },
            { id: "nationality", label: "Nationality", value: data.nationality || "", locked: true },
            { id: "state", label: "State", value: data.state || "" },
            { id: "country_residence", label: "Country of Residence", value: data.country_residence || "", locked: true },
            { id: "allow_name_in_search", label: "Allow Name in Search", value: data.allow_name_in_search ? "Yes" : "No" },
          ]);
        } else {
          setProfileData([
            { id: "screen_name", label: "Screen Name", value: data.screen_name || profile?.screenName || "", locked: true },
            { id: "email", label: "Email", value: data.email || "", locked: true },
            { id: "allow_name_in_search", label: "Allow Name in Search", value: data.allow_name_in_search ? "Yes" : "No" },
          ]);
        }

        // ✅ Handle profile image - use presigned URL from API (profile_picture or id_document)
        const pic = data.profile_picture ?? data.id_document;
        if (pic && typeof pic === "string" && pic.startsWith("http")) {
          setProfileImage(pic);
        } else if (pic && typeof pic === "string" && !pic.includes("/null")) {
          setProfileImage(pic.startsWith("http") ? pic : `${BACKEND_BASE}${pic}`);
        }

        // ✅ Update context with fresh data
        setProfile((prev) => ({
          ...prev,
          ...data,
          screenName: data.screen_name || prev?.screenName,
          profilePicture: data.profile_picture
            ? data.profile_picture.startsWith("http")
              ? data.profile_picture
              : `${BACKEND_BASE}${data.profile_picture}`
            : prev?.profilePicture,
          idDocument: data.id_document
            ? data.id_document.startsWith("http")
              ? data.id_document
              : `${BACKEND_BASE}${data.id_document}`
            : prev?.idDocument,
        }));
      } catch (err) {
        console.error("Profile load error:", err);
      }
    };

    fetchProfile();
  }, [profile?.accessToken, profile?.role, profile?.screenName, setProfile]);

  // 🔹 Fetch image counts for contributor galleries
  useEffect(() => {
    const fetchImageCounts = async () => {
      try {
        // Only fetch for contributors
        if (!profile?.accessToken || profile?.role !== "contributor") {
          return;
        }

        const res = await fetch(`${API_BASE}accounts/body-part-images/counts/`, {
          headers: { Authorization: `Bearer ${profile.accessToken}` },
        });

        if (!res.ok) throw new Error("Failed to fetch image counts");
        const data = await res.json();

        // Transform backend data to gallery cards format - dynamically for ALL categories
        const galleryData = Object.entries(data.by_category || {}).map(([categoryName, count]) => ({
          name: categoryName,
          development: false,
          trophies: 3, // Static for now
          medals: 15,  // Static for now
          ribbons: 12, // Static for now
          photos: count,
        }));

        // Sort by photo count (highest first) or alphabetically
        galleryData.sort((a, b) => b.photos - a.photos || a.name.localeCompare(b.name));

        setGalleries(galleryData);
      } catch (err) {
        console.error("❌ Failed to fetch image counts:", err);
        // Fallback to empty array if fetch fails
        setGalleries([]);
      }
    };

    fetchImageCounts();
  }, [profile?.accessToken, profile?.role]);

  // 🔹 Fetch joined contests
  useEffect(() => {
    const fetchJoinedContests = async () => {
      try {
        if (!profile?.accessToken || profile?.role !== "contributor") return;
        
        setLoadingContests(true);
        const res = await fetch(`${API_BASE}accounts/contests/my-contests/`, {
          headers: { Authorization: `Bearer ${profile.accessToken}` },
        });

        if (!res.ok) throw new Error("Failed to fetch contests");
        const data = await res.json();
        setJoinedContests(data);
      } catch (err) {
        console.error("❌ Failed to fetch contests:", err);
      } finally {
        setLoadingContests(false);
      }
    };

    fetchJoinedContests();
  }, [profile?.accessToken, profile?.role]);

  const toggleEditMode = (id) => {
    setEditMode((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleChange = (id, newValue) => {
    setProfileData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, value: newValue } : item))
    );
  };

  const handleSave = async (id) => {
    try {
      if (!profile?.accessToken) return;

      const item = profileData.find((field) => field.id === id);
      if (!item) return;

      // Backend payload expects snake_case
      const payload = {};
      if (id === "allow_name_in_search") {
        payload[id] = item.value.toLowerCase() === "yes";
      } else {
        payload[id] = item.value;
      }

      const res = await fetch(`${API_BASE}accounts/profile/me/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${profile.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Update failed");
      const updated = await res.json();

      setProfile((prev) => ({ ...prev, ...updated }));

      // Update field with confirmed value
      setProfileData((prev) =>
        prev.map((field) =>
          field.id === id
            ? {
                ...field,
                value:
                  id === "allow_name_in_search"
                    ? updated.allow_name_in_search
                      ? "Yes"
                      : "No"
                    : updated[id] || field.value,
              }
            : field
        )
      );

      setEditMode((prev) => ({ ...prev, [id]: false }));
    } catch (err) {
      console.error("❌ Save failed:", err);
      alert("Failed to save changes");
    }
  };

  const handleImageClick = () => fileInputRef.current.click();

  const handleImageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Local preview
      const reader = new FileReader();
      reader.onload = (ev) => setProfileImage(ev.target.result);
      reader.readAsDataURL(file);

      try {
        if (!profile?.accessToken) return;

        const formData = new FormData();
        formData.append("profile_picture", file);

        const res = await fetch(`${API_BASE}accounts/profile/me/`, {
          method: "PATCH",
          headers: { Authorization: `Bearer ${profile.accessToken}` },
          body: formData,
        });

        if (!res.ok) throw new Error("Failed to upload image");
        const updated = await res.json();

        const rawUrl = updated.profile_picture ?? updated.id_document;
        const imgUrl = rawUrl && String(rawUrl) !== "null"
          ? (String(rawUrl).startsWith("http") ? String(rawUrl) : `${BACKEND_BASE}${rawUrl}`)
          : null;
        if (imgUrl && !imgUrl.includes("/null")) setProfileImage(imgUrl);
        setProfile((prev) => ({ 
          ...prev, 
          profilePicture: updated.profile_picture 
            ? (updated.profile_picture.startsWith("http") 
                ? updated.profile_picture 
                : `${BACKEND_BASE}${updated.profile_picture}`)
            : prev?.profilePicture,
          idDocument: updated.id_document
            ? (updated.id_document.startsWith("http")
                ? updated.id_document
                : `${BACKEND_BASE}${updated.id_document}`)
            : prev?.idDocument
        }));
      } catch (err) {
        console.error("Image upload failed:", err);
      }
    }
  };

  return (
    <div className="flex min-h-screen bg-black text-white">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 lg:ml-64 w-full">
        <Topbar pageTitle="My Profile" />

        <div className="flex flex-col md:flex-row w-full max-w-6xl mx-auto">
          {/* LEFT PROFILE SECTION */}
          <div className="w-full md:w-1/2 bg-[#1C1C1E] rounded-xl mb-6 md:mb-0 p-6">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-[#CEB46A] shadow">
                  {profileImage && !profileImage.includes("/null") ? (
                    <img
                      src={profileImage}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                      onError={() => setProfileImage(null)}
                    />
                  ) : (
                    <div className="bg-[#333] w-full h-full" />
                  )}
                </div>
                <button
                  className="absolute bottom-3 right-2 bg-[#CEB46A] p-2 rounded-full text-black shadow"
                  onClick={handleImageClick}
                >
                  <TbEdit size={16} />
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  className="hidden"
                  accept="image/*"
                />
              </div>
            </div>

            {/* Render fields */}
            {profileData.map((item) => (
              <div key={item.id} className="mb-6 border-b pb-2">
                <div className="text-gray-400 text-sm">{item.label}</div>
                <div className="flex justify-between items-center">
                  {editMode[item.id] && !item.locked ? (
                    <input
                      type="text"
                      value={item.value}
                      onChange={(e) => handleChange(item.id, e.target.value)}
                      onBlur={() => handleSave(item.id)} // save on blur
                      className="font-semibold bg-[#1C1C1E] text-lg border-b border-blue-500 focus:outline-none w-full"
                    />
                  ) : (
                    <div className="font-semibold text-lg">{item.value}</div>
                  )}
                  {!item.locked && (
                    <button
                      className="text-[#CEB46A] ml-2"
                      onClick={() => toggleEditMode(item.id)}
                    >
                      <TbEdit size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* W-9 Tax Form Section - Only for Contributors */}
            {profile?.role === "contributor" && (
              <div className="mt-6 pt-6 border-t border-gray-700">
                <W9Form 
                  onCompletion={() => {
                    // Refresh profile data after W-9 completion
                    window.location.reload();
                  }}
                  showModal={false}
                />
              </div>
            )}

          </div>

          {/* RIGHT GALLERIES & CONTESTS SECTION - Only for Contributors */}
          {profile?.role === "contributor" && (
            <div className="flex-1 md:w-1/2 flex flex-col items-center gap-6 mt-4 md:mt-0 md:pl-6 overflow-y-auto max-h-[85vh]">
              {/* Galleries Section */}
              {galleries.length > 0 && (
                <div className="w-full flex flex-col items-center gap-2">
                  <h2 className="text-[#CEB46A] text-xl font-bold mb-2 self-start pl-4">My Galleries</h2>
                  {galleries.map((gallery, index) => (
                    <div
                      key={index}
                      className="relative bg-[#CEB46A] rounded-xl px-4 py-2 w-full max-w-[500px] shadow-md h-[100px] flex flex-col justify-between"
                    >
                      <div className="flex justify-center items-center gap-2 text-black -mt-2">
                        <h3 className="text-lg font-bold">{gallery.name}</h3>
                        <FaCamera className="text-black text-sm" />
                        <span className="text-sm font-semibold">x{gallery.photos}</span>
                      </div>
                      <div className="flex justify-between items-end mt-1">
                        {!gallery.development && (
                          <span className="bg-green-500 border border-black text-white text-xs font-semibold px-2.5 py-[1px] rounded-sm shadow-sm">
                            Active
                          </span>
                        )}
                        <div className="flex gap-2">
                          <div className="flex items-center gap-1 bg-black text-white px-3 py-[2px] rounded-full text-xs font-semibold">
                            <FaTrophy className="text-[#CEB46A]" /> x{gallery.trophies}
                          </div>
                          <div className="flex items-center gap-1 bg-black text-white px-3 py-[2px] rounded-full text-xs font-semibold">
                            <FaMedal className="text-[#CEB46A]" /> x{gallery.medals || 15}
                          </div>
                          <div className="flex items-center gap-1 bg-black text-white px-3 py-[2px] rounded-full text-xs font-semibold">
                            <FaStar className="text-[#CEB46A]" /> x{gallery.ribbons}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Joined Contests Section */}
              <div className="w-full flex flex-col items-center gap-2">
                <h2 className="text-[#CEB46A] text-xl font-bold mb-2 self-start pl-4">Joined Contests</h2>
                {loadingContests ? (
                  <p className="text-gray-400">Loading contests...</p>
                ) : joinedContests.length > 0 ? (
                  joinedContests.map((contest) => (
                    <div
                      key={contest.id}
                      className="relative bg-[#1C1C1E] border border-[#CEB46A] rounded-xl px-4 py-3 w-full max-w-[500px] shadow-md flex justify-between items-center"
                    >
                      <div className="flex flex-col">
                        <h3 className="text-[#CEB46A] font-bold">{contest.title}</h3>
                        <p className="text-xs text-gray-400">{contest.category}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-semibold bg-[#CEB46A] text-black px-2 py-1 rounded">
                          {new Date(contest.start_time) > new Date() ? 'Upcoming' : 'Live'}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 italic">No joined contests yet.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
